"use client";

import React from "react";

type OpeningsLogoSize = "sm" | "md" | "lg";

type OpeningsLogoSizeConfig = {
  symbol: number;
  wordmark: number;
  /** Pre-computed wordmark width (aspect ratio 150:21.5 from SVG viewBox) */
  wordmarkWidth: number;
  gap: number;
};

// Wordmark SVG viewBox = "0 10.5 150 21.5" → aspect ratio 150/21.5
const WORDMARK_RATIO = 150 / 21.5;

const sizeMap: Record<OpeningsLogoSize, OpeningsLogoSizeConfig> = {
  sm: { symbol: 20, wordmark: 12, wordmarkWidth: Math.round(12 * WORDMARK_RATIO), gap: 4 },
  md: { symbol: 26, wordmark: 16, wordmarkWidth: Math.round(16 * WORDMARK_RATIO), gap: 5 },
  lg: { symbol: 34, wordmark: 22, wordmarkWidth: Math.round(22 * WORDMARK_RATIO), gap: 7 },
};

/**
 * The logo never follows the OS color scheme (Oct 2026). The dashboard,
 * staff and onboarding have no dark theme, and landing pins
 * `color-scheme: light`, so a `prefers-color-scheme: dark` swap painted a
 * white logo on a near-white page — on `/login` under a dark OS it was
 * invisible — and staff ships no `logo-white.svg` to swap to at all.
 * A caller on a dark background opts in instead with `useDarkSymbol` /
 * `useDarkWordmark` (the landing footer does).
 */
export interface OpeningsLogoProps {
  size?: OpeningsLogoSize;
  symbolSrc?: string;
  symbolDarkSrc?: string;
  useDarkSymbol?: boolean;
  wordmarkSrc?: string;
  wordmarkDarkSrc?: string;
  useDarkWordmark?: boolean;
  showSymbol?: boolean;
  showWordmark?: boolean;
  symbolAlt?: string;
  wordmarkAlt?: string;
  className?: string;
  style?: React.CSSProperties;
  symbolClassName?: string;
  wordmarkClassName?: string;
  symbolStyle?: React.CSSProperties;
  wordmarkStyle?: React.CSSProperties;
}

export function OpeningsLogo({
  size = "sm",
  symbolSrc = "/symbol.png",
  symbolDarkSrc = "/symbol-white.png",
  useDarkSymbol = false,
  wordmarkSrc = "/logo.svg",
  wordmarkDarkSrc = "/logo-white.svg",
  useDarkWordmark = false,
  showSymbol = true,
  showWordmark = true,
  symbolAlt = "Openings symbol",
  wordmarkAlt = "Openings wordmark",
  className,
  style,
  symbolClassName,
  wordmarkClassName,
  symbolStyle,
  wordmarkStyle,
}: OpeningsLogoProps) {
  const config = sizeMap[size] ?? sizeMap.md;

  const wrapperStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: config.gap,
    lineHeight: 1,
    ...style,
  };

  const resolvedSymbolStyle: React.CSSProperties = {
    height: config.symbol,
    width: config.symbol, // 1:1 square — reserve exact space to prevent CLS
    display: "block",
    ...symbolStyle,
  };

  const resolvedWordmarkStyle: React.CSSProperties = {
    height: config.wordmark,
    width: config.wordmarkWidth, // pre-computed from SVG aspect ratio
    display: "block",
    ...wordmarkStyle,
  };

  return (
    <span className={className} style={wrapperStyle}>
      {showSymbol ? (
        <img
          src={useDarkSymbol ? symbolDarkSrc : symbolSrc}
          alt={symbolAlt}
          className={symbolClassName}
          style={resolvedSymbolStyle}
        />
      ) : null}
      {showWordmark ? (
        <img
          src={useDarkWordmark ? wordmarkDarkSrc : wordmarkSrc}
          alt={wordmarkAlt}
          className={wordmarkClassName}
          style={resolvedWordmarkStyle}
        />
      ) : null}
    </span>
  );
}

export type { OpeningsLogoSize };
